import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Store } from "../store";
import URI from "urijs";
import Parse, { domToReact } from "html-react-parser";
import useResponsive from "../hooks/useResponsive";
import { useTranslation } from "react-i18next";
import useModal from "../hooks/useModal";
import { SubscribeBlock } from "./blocks";
import useNotification from "../hooks/useNotification";
import Cookies from "js-cookie";
import { Settings } from "luxon";

const Menu = ({ location = "navbar", layout }) => {
  const { cookies, config } = Store.useState(s => s);
  const currentLocation = useLocation();
  const { addNotification } = useNotification();
  const { t, i18n } = useTranslation();
  const settings = config.eshop.theme.selected.settings;
  const { isMobile } = useResponsive({ maxWidth: settings?.mobileBreakpoint });

  const menu = config.eshop?.menus?.find(m => m.location === location);
  if (!menu) return <span className={`menu ${location}`} />;

  const getElementForEntry = ({ entry, i }) => {
    switch (entry.type) {
      case "html": {
        const replace = domNode => {
          if (domNode.name === "a" && domNode.attribs.href && !new URI(domNode.attribs.href).is("absolute")) {
            return <Link to={domNode.attribs.href}>{domToReact(domNode.children)}</Link>;
          } else domToReact(domNode);
        };
        return (
          <div className={`html ${entry.className || ""}`} key={i}>
            {Parse(entry.data.html || "", { replace })}
          </div>
        );
      }
      case "image": {
        const Image = <img className={entry.type} src={entry.data.src} alt={entry.data.alt} />;
        const className = `image ${entry.className || ""}`;
        const isExternal = entry.data?.to && new URI(entry.data.to).is("absolute");
        if (entry.data?.to)
          return !isExternal ? (
            <Link key={i} id={entry.id || ""} className={className} to={entry.data?.to || "/"}>
              {Image}
            </Link>
          ) : (
            <a key={i} id={entry.id} className={`link ${entry.className || ""}`} target="_blank" rel="noopener noreferrer" href={entry.data.to}>
              {Image}
            </a>
          );
        else
          return (
            <div key={i} id={entry.id || ""} className={className}>
              {Image}
            </div>
          );
      }
      case "dropdown":
        return (
          <Dropdown
            key={i}
            entry={entry}
            t={t}
            backgroundColor={!isMobile && location === "header" ? settings.header.backgroundColor : settings.backgroundColor}
            isMobile={isMobile}
            getElementForEntry={getElementForEntry}
            location={currentLocation.pathname + currentLocation.search}
            theme={config.eshop?.theme?.selected}
          />
        );
      case "link": {
        const isExternal = entry.data.to && new URI(entry.data.to).is("absolute");
        const isActive = currentLocation.pathname === entry.data.to;
        if (!isExternal)
          return (
            <Link key={i} className={`link${isActive ? " active" : ""}`} to={entry.data.to}>
              {t(entry.data.key)}
            </Link>
          );
        else
          return (
            <a key={i} id={entry.id} className="link external" target="_blank" rel="noopener noreferrer" href={entry.data.to}>
              {entry.data.key}
            </a>
          );
      }
      case "group":
        return (
          <nav key={`group-${i}`} className={`group ${entry.className || ""}`}>
            {entry.entries.map((e, i) => getElementForEntry({ entry: e, i }))}
          </nav>
        );
      case "subscribe": {
        const listRef = config.getProperty("newsletter", "defaultListRef");
        if (!listRef) return null;
        return <Subscribe key={i} config={config} isMobile={isMobile} listRef={listRef} entry={entry} t={t} />;
      }
      case "privacyConsent": {
        return (
          <button type="button" className="openCookies" key={i} onClick={e => cookies.openModal()}>
            Cookies
          </button>
        );
      }
      case "languageSelector": {
        return <LanguageSelector key={i} styles={menuStyle} config={config} i18n={i18n} addNotification={addNotification} />;
      }
      case "socialNetworks": {
        return <SocialNetworks key={i} styles={menuStyle} config={config} i18n={i18n} addNotification={addNotification} />;
      }
      default:
        return null;
    }
  };

  const menuLocation = menu.location;
  const menuStyle = {};
  const isNavbarAndLeftAligned = location === "navbar" && settings.layout.id === "leftAligned";
  const isHeaderSticky = !!settings.header.sticky;
  const isSticky = isNavbarAndLeftAligned && !isHeaderSticky;

  if (menuLocation === "footer" && settings.footer?.backgroundColor) menuStyle.backgroundColor = `var(--${settings.footer.backgroundColor.id})`;
  else if (menuLocation === "topHeader" && settings.superHeader?.backgroundColor) menuStyle.backgroundColor = `var(--${settings.superHeader.backgroundColor.id})`;

  const elements = menu.entries.map((entry, i) => getElementForEntry({ entry, i }));

  if (!elements.length) return null;
  return (
    <nav id={menu.id} style={menuStyle} className={`menu ${isSticky ? "sticky" : ""} ${menu.className} ${menu.location} ${layout || ""} ${isMobile ? "mobile" : ""}`}>
      <div className="content">{elements}</div>
    </nav>
  );
};

export const Dropdown = ({ entry, t, isMobile, getElementForEntry, location, backgroundColor }) => {
  const { data } = entry;
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const handleClick = event => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  });

  return (
    <div className={`dropdown ${entry.className || ""} ${isMobile ? "mobile" : ""}`} ref={wrapperRef}>
      <div className={`${data.main.className || ""} top`}>
        {data.main.data.to && data.main.data.to !== "/" ? (
          <Link to={data.main.data.to}>
            {data.main.data.key && t(`${data.main.data.key}`, data.main.data.key)}
            <button type="button">
              <i
                className="cg-icon-down"
                onClick={e => {
                  e.preventDefault();
                  setIsOpen(!isOpen);
                }}
              />
            </button>
          </Link>
        ) : (
          <button type="button" className="navItem" onClick={e => setIsOpen(!isOpen)}>
            {data.main.data.key && t(`${data.main.data.key}`, data.main.data.key)}
            <i className="cg-icon-down" />
          </button>
        )}
      </div>
      {isOpen ? (
        <div className="subMenu" style={{ backgroundColor: `var(--${backgroundColor?.id})` }}>
          <ul>
            {data.entries &&
              data.entries.map((i, idx) => (
                <li className="entry" key={i.data.key}>
                  {getElementForEntry({ entry: i, i: idx })}
                </li>
              ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export const Subscribe = ({ config, listRef, t, isMobile }) => {
  const { Modal, useModalComponent } = useModal();
  const [isOpen, setIsOpen] = useState(false);
  const styles = config.getModalStyles();

  const defaultListRef = listRef || config.getProperty("newsletter", "defaultListRef");

  useEffect(() => {
    if (isOpen) showModal();
    else hideModal();
  }, [isOpen]);

  const [showModal, hideModal] = useModalComponent(
    () => (
      <Modal ariaHideApp={false} isOpen={isOpen} style={styles} onRequestClose={e => setIsOpen(false)}>
        <div id="subscribeModal">
          <SubscribeBlock isMobile={isMobile} t={t} listRef={defaultListRef} exit={() => setIsOpen(false)} />
        </div>
      </Modal>
    ),
    [isMobile, isOpen, styles, defaultListRef]
  );

  return (
    <button
      type="button"
      onClick={e => {
        setIsOpen(true);
      }}
    >
      {t("subscribe")}
    </button>
  );
};

const LanguageSelector = ({ config, i18n, styles }) => {
  const currentLanguage = i18n.language;
  const languages = config.eshop.preferences.languages.whitelist;
  const [open, setOpen] = useState(false);
  const localRef = useRef();

  const handleClickOutside = event => {
    if (localRef.current && !localRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  const handleChangeLanguage = locale => {
    Cookies.set("locale", locale, { expires: 365 });
    Settings.defaultLocale = locale;
    i18n.changeLanguage(locale);
  };

  useEffect(() => {
    if (languages.indexOf(currentLanguage) === -1) {
      if (languages.length) handleChangeLanguage(languages[0]);
      else handleChangeLanguage("en");
    }
  }, [config.eshop.preferences.languages.whitelist, currentLanguage]);

  if (languages.length < 2) return null;

  return (
    <div className="languageSelector" ref={localRef}>
      <div className="current">
        <button onClick={e => setOpen(!open)} className="current">
          <i className="cg-icon-languages" />
          {currentLanguage}
          <i className="cg-icon-down" />
        </button>
      </div>
      {open ? (
        <div className="entries" style={styles}>
          {languages
            .filter(l => l !== currentLanguage)
            .map(l => (
              <button
                key={l}
                className={"entry" + (i18n.language === l ? "selected" : "")}
                type="button"
                onClick={e => {
                  setOpen(false);
                  handleChangeLanguage(l);
                }}
              >
                {l}
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
};

const SocialNetworks = ({ config }) => {
  return (
    <div className="socialNetworks">
      {config.socials.map((s, idx) => (
        <a href={s.id === "mail" ? `mailto:${s.value}` : s.id === "phone" ? `tel:${s.value}` : s.value} target="_tab" key={s.id} className="socialNetwork">
          <i className={`cg-icon-${s.id}`} />
        </a>
      ))}
    </div>
  );
};

export default Menu;
export { LanguageSelector };
